/**
 * Enhanced Profile Screen
 * UI polished to match provided design
 * LOGIC & FUNCTIONALITY UNCHANGED
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useFocusEffect, CommonActions, useRoute } from '@react-navigation/native';
import Icon from '../components/Icon';
import { authAPI } from '../services/authApi';
import { userAPI } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { wp, hp, ms, vs, spacing, fontSize, iconSize } from '../utils/responsive';

const SettingScreen = ({ navigation, route }) => {
  const { isDarkMode, toggleTheme, colors } = useTheme();
  const routeParams = route?.params || {};
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [referrer, setReferrer] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // Watch for profile update success param and force refresh
  useEffect(() => {
    if (routeParams?.profileUpdated) {
      console.log('✅ Profile update detected in SettingScreen');
      setShowSuccessToast(true);
      
      // If imageTimestamp provided, use it to force cache bust
      if (routeParams?.imageTimestamp) {
        console.log('🔄 Forcing image refresh with timestamp:', routeParams.imageTimestamp);
        setImageTimestamp(routeParams.imageTimestamp);
      }
      
      // Force fetch latest profile data after a short delay to ensure API is ready
      if (routeParams?.forceRefresh) {
        console.log('🔄 Scheduling profile data refresh...');
        // Small delay to ensure backend has processed the update
        const timer = setTimeout(() => {
          console.log('🔄 Fetching updated profile data...');
          fetchUserData(true).catch(err => {
            console.log('⚠️ Failed to fetch after profile update, will retry on next focus');
          });
        }, 500);
        
        // Auto hide toast after 3 seconds
        const toastTimer = setTimeout(() => setShowSuccessToast(false), 3000);
        
        return () => {
          clearTimeout(timer);
          clearTimeout(toastTimer);
        };
      }
      
      // Auto hide toast after 3 seconds (if no forceRefresh)
      const timer = setTimeout(() => setShowSuccessToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [routeParams?.profileUpdated, routeParams?.imageTimestamp, routeParams?.forceRefresh]);

  const fetchUserData = async (skipErrorRedirect = false) => {
    // Don't show loading spinner if we're refreshing after profile update
    if (!skipErrorRedirect) {
      setIsLoading(true);
    }
    
    try {
      const [profile, referrerData] = await Promise.all([
        authAPI.getProfile(),
        userAPI.getReferrer().catch(() => null),
      ]);
      console.log('📥 Profile fetched:', profile);
      console.log('🖼️ Image URL:', profile?.image);
      console.log('📦 Full profile data:', JSON.stringify(profile));
      
      setUserProfile(profile);
      // Only update timestamp if not already set by route param
      if (!routeParams?.imageTimestamp) {
        setImageTimestamp(Date.now());
      }
      setReferrer(referrerData);
      await fetchActiveSubscription();
      console.log('✅ Profile updated successfully');
    } catch (error) {
      console.error('❌ Error fetching profile:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error details:', error.response?.data);
      console.error('Full error:', JSON.stringify(error, null, 2));
      
      // Only redirect on 401 if we're sure the token is invalid
      // Don't redirect if this is just after a profile update or if skipErrorRedirect is true
      if (error.response?.status === 401 && !routeParams?.profileUpdated && !skipErrorRedirect) {
        Alert.alert('Session Expired', 'Please login again', [
          { text: 'OK', onPress: () => {
            const rootNavigation = navigation.getParent() || navigation;
            rootNavigation.reset({
              index: 0,
              routes: [{ name: 'Login' }],
            });
          }}
        ]);
      } else if (error.response?.status === 401) {
        console.log('⚠️ Got 401 but not redirecting - profile update context or skip flag set');
        // Set loading to false and keep existing profile data
        setIsLoading(false);
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActiveSubscription = async () => {
    try {
      const res = await userAPI.getActiveSubscription();
      console.log('Active Plan Response:', res);
      
      // Handle the response structure from API
      let planData = null;
      if (res?.success && res?.data) {
        // New API format with success flag
        const subscription = res.data;
        if (subscription.status === 'APPROVED') {
          planData = {
            ...subscription,
            planName: subscription.plan?.title || subscription.plan?.name || 'Active Plan'
          };
        }
      } else if (res?.data) {
        // Direct data response
        planData = res.data;
      } else {
        // Fallback
        planData = res;
      }
      
      console.log('Processed Active Plan Data:', planData);
      setActivePlan(planData);
    } catch (error) {
      console.error('Error fetching active plan:', error);
      setActivePlan(null);
    }
  };

  useFocusEffect(
    useCallback(() => {
      console.log('🔄 SettingScreen focused - Checking if refresh needed...');
      console.log('📋 Route params:', routeParams);
      
      // Skip auto-fetch if we just came from a profile update
      if (routeParams?.profileUpdated) {
        console.log('⏭️ Skipping auto-fetch - just updated profile');
        // Turn off loading immediately since profile update will handle the refresh
        setIsLoading(false);
        return;
      }
      
      console.log('🔄 Refreshing profile data...');
      fetchUserData();
    }, [routeParams?.profileUpdated])
  );

  const handleLogout = async () => {
    console.log('🔴 LOGOUT BUTTON CLICKED! Starting immediate logout...');
    
    try {
      // Remove token first
      console.log('🔄 Removing auth token...');
      await authAPI.logout();
      console.log('✅ Token removed successfully');
      
      // Navigate to Login screen
      console.log('🔄 Navigating to Login screen...');
      
      // Get root navigation
      const rootNavigation = navigation.getParent() || navigation;
      console.log('📍 Using navigation:', rootNavigation);
      
      // Reset to Login screen
      rootNavigation.reset({
        index: 0,
        routes: [{ name: 'Login' }],
      });
      
      console.log('✅ Navigation to Login completed!');
    } catch (error) {
      console.error('❌ Logout error:', error);
      console.log('⚠️ Attempting fallback navigation...');
      
      // Fallback navigation
      try {
        navigation.navigate('Login');
      } catch (navError) {
        console.error('❌ Fallback navigation failed:', navError);
      }
    }
  };

  if (isLoading) {
    return (
      <View style={[styles.loader, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.loaderText, { color: colors.textSecondary }]}>Loading profile...</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* HEADER */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <TouchableOpacity>
          <Icon name="arrow-back" size={22} color={colors.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.primary }]}>Settings</Text>
        <TouchableOpacity>
          <Icon name="create-outline" size={22} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {/* Success Toast Notification */}
      {showSuccessToast && (
        <View style={styles.successToast}>
          <Icon name="checkmark-circle" size={20} color="#00E676" />
          <Text style={styles.successToastText}>Profile updated successfully</Text>
        </View>
      )}

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: vs(120) }}>
        {/* PROFILE CARD */}
        <View style={[styles.profileCard, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
          <View 
            key={`avatar-${userProfile?.image}-${imageTimestamp}`}
            style={[styles.avatar, { borderColor: colors.primary, backgroundColor: colors.cardBackground }]}
          >
            {(() => {
              // Use single image field from API
              const imageUrl = userProfile?.image;
              
              if (!imageUrl) {
                return <Icon name="person" size={36} color={colors.primary} />;
              }
              
              // Build full URL
              const fullUrl = imageUrl.startsWith('http') 
                ? imageUrl 
                : `https://a77.onetradebot.com/${imageUrl}`;
              
              // Cache busting URL
              const cacheBustedUrl = `${fullUrl}?v=${imageTimestamp}`;
              
              if (Platform.OS === 'web') {
                // PWA: Use img tag with cache busting
                return (
                  <img
                    src={cacheBustedUrl}
                    alt="Profile"
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '35px',
                      objectFit: 'cover',
                    }}
                  />
                );
              }
              
              // Mobile App: Use Image component
              return (
                <Image 
                  source={{ uri: cacheBustedUrl }} 
                  style={styles.avatarImg}
                />
              );
            })()}
          </View>

          <View style={{ flex: 1, marginLeft: 4 }}>
            <Text style={[styles.userName, { color: colors.text }]}>{userProfile?.name || 'User'}</Text>

            <View style={styles.row}>
              <Icon name="mail-outline" size={16} color={colors.primary} />
              <Text style={[styles.subText, { color: colors.textSecondary }]}>{userProfile?.email}</Text>
            </View>

            <View style={styles.row}>
              <Icon name="call" size={16} color={colors.primary} />
              <Text style={[styles.subText, { color: colors.textSecondary }]}>
                {userProfile?.phone ||
                  userProfile?.mobile ||
                  userProfile?.contact ||
                  'Phone not provided'}
              </Text>
            </View>

            <View style={styles.row}>
              <Icon name="card-outline" size={16} color={colors.primary} />
              <Text style={[styles.planText, { color: colors.primary }]}>
                {activePlan?.planName || activePlan?.plan?.title || activePlan?.plan?.name || activePlan?.name || (activePlan ? 'Active Plan' : 'No Active Plan')}
              </Text>
              {(activePlan?.planName || activePlan?.plan?.title || activePlan?.plan?.name || activePlan?.name || activePlan) && (
                <View style={styles.activeBadge}>
                  <Text style={styles.activeText}>ACTIVE</Text>
                </View>
              )}
            </View>
          </View>

          <TouchableOpacity style={[styles.editIcon, { backgroundColor: colors.cardBackground, borderColor: colors.border }]} onPress={() => navigation.navigate('EditProfile')}>
            <Icon name="create-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
        </View>

        {/* ACCOUNT */}
        <Text style={[styles.section, { color: colors.textSecondary }]}>ACCOUNT</Text>
        <Item title="Edit Profile" icon="person-outline" color="#4A90E2" onPress={() => navigation.navigate('EditProfile')} />
        <Item title="Change Password" icon="key-outline" color="#FF9800" onPress={() => navigation.navigate('ChangePassword')} />

        {/* FINANCIAL */}
        <Text style={[styles.section, { color: colors.textSecondary }]}>FINANCIAL</Text>
        <Item title="My Wallet" subtitle="View balance & transactions" icon="wallet" color="#FFD700" onPress={() => navigation.navigate('Wallet')} />
        <Item title="AI Bot Subscription" subtitle={activePlan?.planName || activePlan?.plan?.title || activePlan?.plan?.name || activePlan?.name || (activePlan ? 'Active Plan' : 'No Active Plan')} icon="card" color="#4A90E2" onPress={() => navigation.navigate('Subscription')} />
        <Item title="IB Program" subtitle={referrer?.hasReferrer ? `Referred by: ${referrer.referrer?.name}` : 'Earn from referrals'} icon="people" color="#00E676" onPress={() => navigation.navigate('Referral')} />
        <Item title="Deposit Funds" subtitle="Add money to your account" icon="add-circle" color="#4A90E2" onPress={() => navigation.navigate('Deposit')} />
        <Item title="Withdraw Funds" subtitle="Transfer to your bank" icon="remove-circle" color="#9C27B0" onPress={() => navigation.navigate('Withdrawal')} />

        {/* APP SETTINGS */}
        <Text style={[styles.section, { color: colors.textSecondary }]}>APP SETTINGS</Text>
        <Item title="Dark Mode" subtitle={isDarkMode ? 'Enabled' : 'Disabled'} icon="moon" color="#FFD700" onPress={toggleTheme} />

        {/* SUPPORT */}
        <Text style={[styles.section, { color: colors.textSecondary }]}>SUPPORT</Text>
        <Item title="Help & Support" icon="help-circle-outline" color="#4A90E2" onPress={() => navigation.navigate('HelpSupport')} />
        <Item title="Terms & Conditions" icon="document-text-outline" color="#00E676" onPress={() => navigation.navigate('Terms')} />
        <Item title="Privacy Policy" icon="shield-outline" color="#9C27B0" onPress={() => navigation.navigate('Privacy')} />
        <Item title="About" subtitle="Version 1.0.0" icon="information-circle-outline" color="#FF9800" onPress={() => navigation.navigate('About')} />

        {/* LOGOUT */}
        <TouchableOpacity 
          style={[styles.logoutBtn, { backgroundColor: isDarkMode ? '#2A0F16' : '#FFE5E5' }]} 
          onPress={() => {
            console.log('🔴🔴🔴 LOGOUT BUTTON PRESSED! 🔴🔴🔴');
            handleLogout();
          }}
          activeOpacity={0.7}
        >
          <Icon name="log-out-outline" size={20} color="#FF5A5A" />
          <Text style={[styles.logoutText, { color: isDarkMode ? '#FF5A5A' : '#DC2626' }]}>Logout</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const Item = ({ title, subtitle, icon, color, onPress }) => {
  const { colors } = useTheme();
  return (
    <TouchableOpacity style={[styles.card, { backgroundColor: colors.cardBackground }]} onPress={onPress}>
      <View style={[styles.iconBox, { backgroundColor: `${color}22` }]}>
        <Icon name={icon} size={22} color={color} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={[styles.cardTitle, { color: colors.text }]}>{title}</Text>
        {subtitle && <Text style={[styles.cardSub, { color: colors.textSecondary }]}>{subtitle}</Text>}
      </View>
      <Icon name="chevron-forward" size={18} color={colors.textTertiary} />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#050816' },

  loader: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#050816' },
  loaderText: { color: '#8B94B8', marginTop: 10 },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  headerTitle: { color: '#6FB1FF', fontSize: 20, fontWeight: '600' },

  successToast: {
    position: 'absolute',
    top: 80,
    left: spacing.base,
    right: spacing.base,
    backgroundColor: '#1D7A46',
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    zIndex: 1000,
    shadowColor: '#00E676',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  successToastText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },

  profileCard: {
    backgroundColor: '#101735',
    marginHorizontal: spacing.base,
    marginTop: 20,
    marginBottom: spacing.base,
    borderRadius: 20,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  avatar: {
    width: 70,
    height: 70,
    borderRadius: 35,
    borderWidth: 2,
    borderColor: '#6FB1FF',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1C2A52',
    overflow: 'hidden',
    position: 'relative',
  },
  avatarImg: { 
    width: 70, 
    height: 70, 
    borderRadius: 35,
    resizeMode: 'cover',
  },
  editIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#1e3a5f',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(111, 177, 255, 0.3)',
  },

  userName: { color: '#fff', fontSize: 18, fontWeight: '700', marginBottom: 4 },
  row: { flexDirection: 'row', alignItems: 'center', marginTop: 5, gap: 6 },
  subText: { color: '#8B94B8', fontSize: 13 },
  planText: { color: '#6FB1FF', fontSize: 13, fontWeight: '600' },

  activeBadge: {
    backgroundColor: '#1D7A46',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginLeft: 8,
  },
  activeText: { color: '#3DFF9F', fontSize: 10, fontWeight: '700' },

  section: {
    color: '#8B94B8',
    fontSize: 12,
    marginLeft: spacing.base,
    marginTop: 24,
    marginBottom: 12,
    letterSpacing: 1,
  },

  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#111A3A',
    marginHorizontal: spacing.base,
    marginBottom: 12,
    padding: spacing.base,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.05)',
  },
  iconBox: {
    width: 46,
    height: 46,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  cardTitle: { color: '#fff', fontSize: 14, fontWeight: '500' },
  cardSub: { color: '#8B94B8', fontSize: 11, marginTop: 2 },

  logoutBtn: {
    backgroundColor: '#2A0F16',
    margin: spacing.base,
    borderRadius: 20,
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,82,82,0.35)',
  },
  logoutText: { color: '#FF5A5A', fontSize: 16, fontWeight: '600' },
});

export default SettingScreen;
